import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../css/MilaAiPopup.css";
import * as signalR from "@microsoft/signalr";
import html2pdf from "html2pdf.js";

import "katex/dist/katex.min.css";

import SearchBar from "./SearchBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CommentModal from "./CommentModal";
import apiClient from "../../business/network/ApiClient";
import NotificationScheduler from "./NotificationScheduler";
import { CurrentUser } from "../../business/helper/CurrentUser";
import utils from "../../business/helper/Utils";
import { GrtAnalyticsGA4 } from "./GrtAnalyticsGA4";
import icon from "../../images/logo.svg";
import Suggested from "./Suggested";
import clsx from "clsx";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import Modal from "../design/Shared/Modal";
import UserProfile from "../design/UserProfile";
import Question from "../design/Chat/Question";
import Response from "../design/Chat/Response";
import { isMenuDisabled } from "../../business/helper/Singleton";
import stopIcon from "../../images/icons/stop.svg";
import AutoGrowTextArea from "./AutoGrowTextArea";
import NounAwareTextArea from "./NounAwareTextArea";
import { useNounRecognition } from "../../hooks/useNounRecognition";
import useScheduledFeedPaging from "../../hooks/useScheduledFeedPaging";
import { useMentionCompletion } from "../../hooks/useMentionCompletion";
import MilaUploader from "./MilaUploader";
import Button from "../design/Shared/Button";
import { useMilaContext } from "../context/useMilaContext";
import FilesContainer from "../design/Chat/FilesContainer";
import PromptSelectionSchedule from "../design/Query/PromptSelectionSchedule";
import { discussionUploadedFiles } from "../design/Query/ScheduleFilesWarning";
import { buildExcelExportPayload, responseTables } from "../../business/helper/ExcelExport";
import { useOnboarding, ONBOARDING_STATUS } from "../context/OnboardingContext";
import OnboardingSuggestedResponses from "./OnboardingSuggestedResponses";
import OnboardingTour from "./OnboardingTour";

export const baseUrl = `${process.env.REACT_APP_CURRENT_MILAAPI}`;

const CustomErrorToast = ({ message, closeToast }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  }}>
    <span style={{ flex: 1, paddingLeft: '26px', paddingRight: '26px', paddingTop: '0px', paddingBottom: '0px', margin: '0px' }}>{message}</span>
    <button
      onClick={closeToast}
      style={{
        background: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '21px',
        height: '21px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#c42c3e',
        fontSize: '18px',
        fontWeight: 'bold',
        lineHeight: '1',
        paddingTop: '2px',
        marginRight: '18px'
      }}
    >
      ×
    </button>
  </div>
);

const milaUnavailableText = "Mila is currently unavailable. Please try again.";

const MilaAiPage = ({
  externalMessage,
  setFavorites,
  favorites,
  setMilaHistoricData,
  inputConversation,
  onConversationLoaded,
  onResetDone,
  reset,
  resetRequestId,
  inputThreadId,
  onExternalMessageHandled,
  onFilesUploaded,
  onResetUploader,
  setCurrentMessageInBox
}) => {
  const [message, setMessage] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [searchMessage, setSearchMessage] = useState("");
  const [conversation, setConversation] = useState([]);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [connection, setConnection] = useState(null);
  const [enableInput, setEnableInput] = useState(true);
  const [requestId, setRequestId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [commentId, setCommmentId] = useState(null);
  const [liking, setLiking] = useState(true);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showPromptSelectionSchedule, setShowPromptSelectionSchedule] = useState(false);

  const [loadingExcel, setLoadingExcel] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [isMenuActive, setIsMenuActive] = useState(isMenuDisabled.state);
  const [activeConnection, setActiveConnection] = useState(null);
  const [isStopReady, setIsStopReady] = useState(false);
  const [commentIndex, setCommentIndex] = useState(0);
  const [showSuggested, setShowSuggested] = useState(true);
  const [uploadFilesFunction, setUploadFilesFunction] = useState(null);
  const [resetUploaderFunction, setResetUploaderFunction] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [titleUploadFile, setTitleUploadFile] = useState("");
  const [charts, setCharts] = useState({});
  const [tables, setTables] = useState({});
  const [processingMessage, setProcessingMessage] = useState('');

  const milaContext = useMilaContext();

  // ── Onboarding (parallel workflow — never touches the normal ProcessPrompt path) ──
  const onboarding = useOnboarding();
  const onboardingRef = useRef(onboarding);
  useEffect(() => {
    onboardingRef.current = onboarding;
  });
  const [onboardingConnection, setOnboardingConnection] = useState(null);
  const [onboardingSuggestions, setOnboardingSuggestions] = useState([]);
  const [showTour, setShowTour] = useState(false);

  // ── Noun recognition + @mention ──────────────────────────────────────────
  const nounUserId  = userData?.userId ?? null;
  const nounOuId    = milaContext?.rootOu?.rootOu ?? userData?.ouId ?? null;
  const rootOU      = nounOuId;

  const [mentionQuery, setMentionQuery] = useState(null);
  const { suggestions: mentionSuggestions, isLoading: isMentionLoading } =
    useMentionCompletion(mentionQuery, nounOuId);

  const { detections, updateAfterReplace, updateAfterMention } =
    useNounRecognition(message, nounUserId, nounOuId, !!mentionQuery);

  const handleNounReplace = useCallback((newText, replacedDetection, candidateText) => {
    updateAfterReplace(replacedDetection, candidateText, newText);
    setMessage(newText);
  }, [updateAfterReplace]);

  const handleMentionApply = useCallback((newText) => {
    updateAfterMention(newText);
    setMentionQuery(null);
    setMessage(newText);
  }, [updateAfterMention]);

  const messageStartTime = useRef(null);

  const fallbackTimer = useRef(null);
  const fallbackIndex = useRef(0);
  const fallbackMessages = useRef([]);

  const startFallbackCycle = (baseMessage) => {
    if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    fallbackIndex.current = 0;

    const cycle = () => {
      if (!fallbackMessages.current?.length) return;

      const messages = fallbackMessages.current;
      const nextMessage = fallbackIndex.current < messages.length
        ? messages[fallbackIndex.current]
        : baseMessage;

      fallbackIndex.current = fallbackIndex.current < messages.length
        ? fallbackIndex.current + 1
        : 0;

      setProcessingMessage(nextMessage);

      const typingDuration = nextMessage.length * 30;
      const holdDuration = Math.floor(Math.random() * 1000) + 2500;
      const randomDelay = Math.floor(Math.random() * 10000) + 5000;

      fallbackTimer.current = setTimeout(cycle, typingDuration + holdDuration + randomDelay);
    };

    const initialDelay = Math.floor(Math.random() * 10000) + 5000;
    fallbackTimer.current = setTimeout(cycle, initialDelay);
  };

  const MIN_DISPLAY_MS = 2000;
  const MAX_DISPLAY_MS = 3500;

  function getRandomDuration() {
    return Math.floor(Math.random() * (MAX_DISPLAY_MS - MIN_DISPLAY_MS + 1)) + MIN_DISPLAY_MS;
  }

  const handleUploadError = (errorMessage) => {
    toast(<CustomErrorToast message={<div dangerouslySetInnerHTML={{ __html: errorMessage }} />} closeToast={() => toast.dismiss()} />, {
      position: "top-center",
      autoClose: 25000,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      closeButton: false,
      style: {
        background: 'linear-gradient(135deg, #d83f44 0%, #d83f44 100%)',
        borderRadius: '40px',
        color: 'white',
        fontSize: '13px',
        fontWeight: '500',
        padding: '0px 0px 0px 0px',
        boxShadow: '0 4px 12px rgba(215, 52, 72, 0.3)',
        border: 'none',
        minWidth: '500px',
        maxWidth: '600px'
      }
    });
  };

  const handleFilesReady = (uploadFunction) => {
    setUploadFilesFunction(uploadFunction);
  };

  useEffect(() => {
    console.log("Mila Ai Context :");
    console.log(milaContext);

    const event = () => setIsMenuActive(isMenuDisabled.state);
    isMenuDisabled.subscribe(event);

    return () => {
      isMenuDisabled.unsubscribe(event);
    };
  }, []);

  useEffect(() => {
    isMenuDisabled.state = !enableInput;
  }, [enableInput]);

  const responseEndRef = useRef(null);
  // The scroll container of the conversation. A scheduled prompt's feed opens on its latest
  // page and loads the earlier answers as the reader scrolls up, like a messaging history.
  const conversationScrollRef = useRef(null);
  const prependConversation = useCallback(
    (entries) => setConversation((prev) => [...entries, ...(prev ?? [])]),
    []
  );
  const scheduledFeed = useScheduledFeedPaging({
    scrollRef: conversationScrollRef,
    prepend: prependConversation,
  });

  const notify = (m) => toast.success(m);
  const notifyErr = (m) => toast.error(m);

  const handleAnalytics = (ga) => {
    if (ga) {
      setAnalytics(ga);
    }
  };

  async function cleanLastResponse() {
    const waitingTexts = await apiClient.getWaitingTexts();

    setConversation((currentConversation) => {
      if (!currentConversation || currentConversation.length === 0) {
        return currentConversation;
      }

      const lastItem = currentConversation[currentConversation.length - 1];

      if (!lastItem || !lastItem.response) {
        return currentConversation;
      }

      let cleanedResponse = lastItem.response;

      waitingTexts.forEach((text) => {
        cleanedResponse = cleanedResponse.replace(text, "");
      });


      cleanedResponse = utils.sanitizeMarkdown(cleanedResponse);

      if (cleanedResponse !== lastItem.response) {
        return [
          ...currentConversation.slice(0, -1),
          {
            ...lastItem,
            response: cleanedResponse,
          },
        ];
      }

      return currentConversation;
    });
  }

  const exportToPdf = async (content) => {
    const tempDiv = document.createElement("div");
    tempDiv.className = "markdown-pdf-export";

    tempDiv.style.padding = "20px";
    tempDiv.style.fontFamily = "Arial, sans-serif";
    tempDiv.style.maxWidth = "800px";
    tempDiv.style.margin = "0 auto";

    // Make invisible but keep in viewport for html2canvas
    tempDiv.style.position = "fixed";
    tempDiv.style.left = "0";
    tempDiv.style.top = "0";
    tempDiv.style.zIndex = "-1000";
    tempDiv.style.opacity = "0";
    tempDiv.style.pointerEvents = "none";

    const header = document.createElement("div");
    header.innerHTML = `
            <img src="./logoblack.png" alt="GreenRoad" style="height:36px" /><br/>
            <h1 style="color: #243f63; margin-bottom: 20px;">Mila Report</h1>
            <p style="color: #666; margin-bottom: 30px;">Generated on: ${new Date().toLocaleString()}</p>
        `;
    tempDiv.appendChild(header);

    const contentDiv = document.createElement("div");
    contentDiv.style.marginTop = "20px";

    const root = document.createElement("div");

    contentDiv.appendChild(root);
    tempDiv.appendChild(contentDiv);

    // Append to document BEFORE rendering React
    document.body.appendChild(tempDiv);

    const markdownComponent = (utils.renderWithReports(content
      .replace(/\\\(/g, "$(")
      .replace(/\\\)/g, ")$")
      .replace(/\\\[/g, "$(")
      .replace(/\\\]/g, ")$"), charts, tables, inputThreadId || threadId)
    );

    await import("react-dom").then((ReactDOM) => {
      ReactDOM.render(markdownComponent, root);
    });

    if (Object.keys(charts).length > 0) {
      // Wait for React rendering and chart animations
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    // Reset styles for PDF capture
    tempDiv.style.position = "relative";
    tempDiv.style.opacity = "1";
    tempDiv.style.zIndex = "auto";

    const options = {
      margin: [15, 15],
      filename: "conversation-export.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        putOnlyUsedFonts: true,
      },
    };

    try {
      await html2pdf().from(tempDiv).set(options).save();
      notify("PDF exported successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      notifyErr("Failed to export PDF. Please try again.");
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  function downloadExcel(base64String) {
    const binaryData = atob(base64String);

    const byteArray = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      byteArray[i] = binaryData.charCodeAt(i);
    }

    const blob = new Blob([byteArray], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `export_${Math.random()
      .toString(36)
      .substring(2, 15)}.xlsx`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  useEffect(() => {
    if (inputConversation) {
      // Re-opened onboarding thread: tag entries so they render without the response toolbox
      const isOnboardingHistory =
        !!onboarding.onboardingThreadId &&
        (inputThreadId || inputConversation.threadId) === onboarding.onboardingThreadId;
      setConversation(
        isOnboardingHistory && Array.isArray(inputConversation)
          ? inputConversation.map((c) => ({ ...c, isOnboarding: true }))
          : inputConversation
      );
      setMessage("");
      setCurrentMessageInBox("");
      setSearchMessage("");
      setThreadId(inputThreadId);
      // A scheduled feed arrives with its paging cursor (Scheduled.jsx); anything else closes it.
      scheduledFeed.open(inputConversation.scheduled ?? null);

      if (inputConversation.charts && Array.isArray(inputConversation.charts)) {
        setCharts(prevCharts => ({
          ...prevCharts,
          [inputConversation.threadId]: inputConversation.charts
        }));
      }

      if (inputConversation.tables && Array.isArray(inputConversation.tables)) {
        setTables(prevTables => ({
          ...prevTables,
          [inputConversation.threadId]: inputConversation.tables
        }));
      }
    }
  }, [inputConversation]);

  const startNewDiscussion = async () => {
    await apiClient.removeRequestContext(requestId);
  };

  useEffect(() => {
    if (reset === true) {
      setShowSuggested(true);
      setConversation([]);
      scheduledFeed.close();
      setMessage("");
      setCurrentMessageInBox("");
      setSearchMessage("");
      setThreadId(null);
      setRequestId(null);
      startNewDiscussion();
      onResetDone();
    }
  }, [reset]);

  useEffect(() => {
    setRequestId(null);
  }, [resetRequestId]);

  useEffect(() => {
    if (externalMessage) {
      setMessage(externalMessage);
      setSearchMessage(externalMessage);
      setThreadId(null);
      onExternalMessageHandled();
    }
  }, [externalMessage]);

  const chartsRef = useRef(charts);

  useEffect(() => {
    chartsRef.current = charts; // Keep ref in sync with state
  }, [charts]);

  const tablesRef = useRef(tables);

  useEffect(() => {
    tablesRef.current = tables; // Keep ref in sync with state
  }, [tables]);

  useEffect(() => {
    let uData = CurrentUser.GetCurrentUserData();
    console.log(uData);
    if (uData) {
      setUserData(uData);
    }

    var userToken = CurrentUser.GetToken();

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/milahub`, {
        accessTokenFactory: async () => {
          const token = userToken?.access_token;
          return token;
        },
      })
      .withAutomaticReconnect([0, 500, 2000, 5000, 10000, 15000, 20000, 25000, 30000])
      .build();

    setConnection(newConnection);

    newConnection.onreconnected(() => {
      console.log("Reconnected to the SignalR hub");
    });

    newConnection.onclose(async (error) => {
      console.log("Disconnected from SignalR hub:", error);
      if (error) {
        const resetMilaUIStates = () => {
          setIsLoading(false);
          console.log("Resetting Mila UI states");
          setEnableInput(true);
          setActiveConnection(null);
        }
        setTimeout(resetMilaUIStates(), 2500);
      }
    });

    newConnection
      .start()
      .then((result) => {
        console.log("Connected to Mila Hub!");

        newConnection.on("FallbackMessages", (message) => {
          if (message) {
            const parsedMessages = JSON.parse(message);
            fallbackMessages.current = parsedMessages;
          }
        });

        newConnection.on("ReceiveMessage", (message) => {
          if (message) {
            let response = {};

            const process = async () => {
              try {
                response = JSON.parse(message);
                if (response.Request_Id) {
                  setRequestId(response.Request_Id);
                }

                if (response.Table) {
                  let tId = response.Thread_Id || inputThreadId || threadId;
                  if (tId) {
                    const currentThreadRefTables = tablesRef.current[tId] || [];
                    let tableIndex = currentThreadRefTables.length;

                    let tableToExport = { ...response.Table };
                    let updatedTables = setTables(prevTables => {
                      console.log('Previous tables:', prevTables);
                      console.log('Thread ID:', tId);
                      const currentThreadTables = prevTables[tId] || [];
                      tableIndex = currentThreadTables.length;
                      const updated = {
                        ...prevTables,
                        [tId]: [...currentThreadTables, tableToExport]
                      };
                      console.log('Table index:', tableIndex);
                      console.log('Updated tables:', updated);

                      return updated;
                    });

                    response.Text = response.Text + `\n\n{{TABLE:${tableIndex}}}\n\n`;
                    response.Table = null;
                  }
                }

                if (response.Chart) {
                  let tId = response.Thread_Id || inputThreadId || threadId;
                  console.log(`Displaying Chart for threadId ${tId} for chart ${response.Chart}`);
                  if (tId) {
                    const currentThreadRefCharts = chartsRef.current[tId] || [];
                    let chartIndex = currentThreadRefCharts.length;
                    let chartToDraw = { ...response.Chart };

                    let updatedCharts = setCharts(prevCharts => {
                      console.log('Previous charts:', prevCharts);
                      console.log('Thread ID:', tId);
                      console.log('Existing for thread:', prevCharts[tId]);

                      const currentThreadCharts = prevCharts[tId] || [];
                      chartIndex = currentThreadCharts.length;

                      const updated = {
                        ...prevCharts,
                        [tId]: [...currentThreadCharts, chartToDraw]
                      };

                      console.log('Chart index:', chartIndex);
                      console.log('Updated charts:', updated);
                      return updated;
                    });

                    response.Text = response.Text + `\n\n{{CHART:${chartIndex}}}\n\n`;
                    response.Chart = null;
                  }
                }

                if (response.IsProcessing === true) {
                  const incomingMessage = response.Message;
                  const now = Date.now();
                  const elapsed = messageStartTime.current ? now - messageStartTime.current : 0;
                  const randomDuration = getRandomDuration();
                  const remaining = randomDuration - elapsed;

                  response.ProcessingMessage = incomingMessage;
                  response.Text = '';

                  if (fallbackTimer?.current) clearTimeout(fallbackTimer.current);

                  if (remaining > 0) {
                    await new Promise(resolve => setTimeout(resolve, remaining));
                  }

                  // Set AFTER the wait
                  setProcessingMessage(incomingMessage);
                  messageStartTime.current = Date.now();

                  if (fallbackMessages?.current?.length > 0) {
                    startFallbackCycle(incomingMessage);
                  }
                }
              } catch (e) {
                console.log(`${e} => ${message}`);
                response.Text =
                  "Something went wrong. Could you ask again?";
                response.IsEnded = true;
                setIsStopReady(false);
                cleanLastResponse();
              }

              if (response.Text || response.IsProcessing === true) {
                setConversation((prevConversation) => {
                  const lastConversationEntry = prevConversation[prevConversation.length - 1];
                  return [
                    ...prevConversation.slice(0, -1),
                    {
                      ...lastConversationEntry,
                      response: lastConversationEntry.response + response.Text,
                      isProcessing: response.IsProcessing,
                      processingMessage: response.ProcessingMessage
                    },
                  ];
                });
              }

              if (responseEndRef.current) {
                responseEndRef.current.scrollIntoView({ behavior: "smooth" });
              }

              if (response.IsEnded === true) {
                console.log("Conversation ended");
                if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
                fallbackIndex.current = 0;
                fallbackMessages.current = [];
                setProcessingMessage('');
                setEnableInput(true);
                setIsLoading(false);
                setIsStopReady(false);
                cleanLastResponse();
              }
            };
            process();
          }
        });

        newConnection.on("SetRequestId", (message) => {
          if (message) {
            setRequestId(message);
          }
        });

      })
      .catch((e) => console.log("Connection failed reason : ", e));

    return () => {
      if (newConnection) {
        newConnection.stop();
      }
    };
  }, []);

  // ── Onboarding: second, lazy SignalR connection to /onboardinghub (design §5.4) ──
  useEffect(() => {
    if (!onboarding.isConversationActive) {
      return;
    }

    const userToken = CurrentUser.GetToken();
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/onboardinghub`, {
        accessTokenFactory: async () => userToken?.access_token,
      })
      .withAutomaticReconnect([0, 500, 2000, 5000, 10000])
      .build();

    conn.on("ReceiveMessage", (message) => {
      if (!message) return;
      try {
        const response = JSON.parse(message);

        if (response.Text) {
          setConversation((prev) => {
            const last = prev[prev.length - 1];
            // Stream only into the pending entry created for this turn — after a history
            // reload the last entry is a persisted turn and appending to it duplicates it.
            if (!last || last.pending !== true) {
              return [...prev, { question: null, response: response.Text, isOnboarding: true, pending: true }];
            }
            return [
              ...prev.slice(0, -1),
              { ...last, response: (last.response || "") + response.Text },
            ];
          });
          if (responseEndRef.current) {
            responseEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }

        if (response.IsEnded === true) {
          setConversation((prev) => {
            const last = prev[prev.length - 1];
            if (!last || last.pending !== true) return prev;
            // A turn that streamed nothing (e.g. a re-entry the backend chose not to greet
            // twice) leaves an empty bubble behind — drop the placeholder instead.
            if (!last.question && !last.response) return prev.slice(0, -1);
            return [...prev.slice(0, -1), { ...last, pending: false }];
          });
          setEnableInput(true);
          setIsLoading(false);
          setIsStopReady(false);
          setOnboardingSuggestions(response.SuggestedResponses || []);
          onboardingRef.current?.applyServerUpdate(response);

          // The backend persists onboarding turns itself (IsOnboarding = true) —
          // just refresh the history sidebar.
          const u = CurrentUser.GetCurrentUserData();
          if (u?.userId) {
            apiClient
              .getMilaHistoric(u.userId)
              .then((h) => setMilaHistoricData(h))
              .catch(() => {});
          }
        }
      } catch (e) {
        console.log("Onboarding message parse failed:", e, message);
      }
    });

    conn.on("TriggerAction", (action) => {
      if (action === "start_tour") {
        setShowTour(true);
      }
    });

    conn
      .start()
      .then(() => {
        console.log("Connected to Onboarding Hub!");
        setOnboardingConnection(conn);
      })
      .catch((e) => console.log("Onboarding hub connection failed:", e));

    return () => {
      conn.stop();
      setOnboardingConnection(null);
    };
  }, [onboarding.isConversationActive]);

  // Kick off the scripted conversation ("__start__" / "__resume__") once the hub is connected
  useEffect(() => {
    const send = async () => {
      if (!onboardingConnection || !onboarding.isConversationActive) return;

      const autoPrompt = onboarding.consumeAutoPrompt();
      if (!autoPrompt) return;

      // Auto-start fires at landing (no user interaction) — userData state may not be set yet
      const uData = userData || CurrentUser.GetCurrentUserData();

      setOnboardingSuggestions([]);
      setConversation((prev) => [...prev, { question: null, response: "", isOnboarding: true, pending: true }]);
      setEnableInput(false);
      setIsLoading(true);

      try {
        await onboardingConnection.invoke(
          "ProcessOnboardingPrompt",
          Number(uData?.ouId),
          autoPrompt,
          null,
          onboarding.onboardingThreadId,
          Number(uData?.userId),
          milaContext?.language || "en-GB"
        );
      } catch (e) {
        console.error("Onboarding auto prompt failed:", e);
        setEnableInput(true);
        setIsLoading(false);
      }
    };
    send();
  }, [onboarding.autoPromptVersion, onboardingConnection]);

  const handleTourComplete = async () => {
    setShowTour(false);
    try {
      if (onboardingConnection) {
        setConversation((prev) => [...prev, { question: null, response: "", isOnboarding: true, pending: true }]);
        setEnableInput(false);
        setIsLoading(true);
        await onboardingConnection.invoke(
          "NotifyTourComplete",
          onboarding.onboardingThreadId
        );
      }
    } catch (e) {
      console.error("NotifyTourComplete failed:", e);
      setEnableInput(true);
      setIsLoading(false);
    }
    analytics?.trackEventAnalytics("Onboarding", "TourCompleted", "click");
  };

  useEffect(() => {
    if (conversation && enableInput && !isLoading) {
      //Saving historic here — while onboarding is ACTIVE the backend persists the turns;
      //once Completed, normal turns on this thread are saved by the frontend again.
      const activeThreadId = inputThreadId || threadId;
      const isActiveOnboardingThread = onboarding.isActiveThread(activeThreadId);
      // An older page of a scheduled feed is a read, not a turn: nothing to save.
      if (inputConversation === null && !isActiveOnboardingThread && !scheduledFeed.prependedRef.current) {
        handleSaveHistoric();
      }
    }
  }, [enableInput, isLoading, conversation]);

  useEffect(() => {
    if (scheduledFeed.prependedRef.current) {
      // Earlier answers were added above: the viewport stays where the reader was
      // (useScheduledFeedPaging restores it); only a new turn scrolls to the end.
      scheduledFeed.prependedRef.current = false;
    } else if (!isLoading && responseEndRef.current) {
      responseEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (conversation !== null) {
      onConversationLoaded();
    }
  }, [isLoading, conversation]);

  useEffect(() => {
    if (commentId && (liking === true || liking === false)) {
      setShowCommentModal(true);
    }
  }, [commentId, liking]);

  const reconnect = async () => {
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      try {
        console.log("Attempting to reconnect...");
        await connection.start();
        console.log("Reconnected successfully.");
      } catch (err) {
        console.error("Reconnect failed:", err);
      }
    } else {
      console.log("Connection is already active.");
    }
  };

  const handleSchedule = async (scheduleConfig) => {
    setShowScheduler(false);
    scheduleConfig.userId = userData?.userId;
    scheduleConfig.ouId = userData?.ouId;
    console.log(threadId, inputThreadId);
    scheduleConfig.sourceThreadId = threadId || inputThreadId;

    var response = await apiClient.setRecurrence(scheduleConfig);
    if (response === true) {
      notify("Your query was scheduled successfully!");
    } else {
      notifyErr("Your query cannot be scheduled or is already scheduled.");
    }
    analytics?.trackEventAnalytics("MilaToolbox", "Schedule Question", "click");
  };

  const handleExportToPdf = (response) => {
    exportToPdf(response);
    analytics?.trackEventAnalytics("MilaToolbox", "ExportPDF", "click");
  };

  const handleExportToExcel = async (response, tablesOfResponse) => {
    setLoadingExcel(true);
    try {
      const payload = buildExcelExportPayload(response, tablesOfResponse);
      let base64 = await apiClient.exportResponseToExcel(
        payload.response,
        payload.tables,
        userData?.ouId,
        userData?.userId
      );
      if (base64?.length > 0) {
        downloadExcel(base64);
      } else {
        notifyErr(
          "Mila is currently unable to generate your Excel file. Please try again shortly."
        );
      }

      analytics?.trackEventAnalytics("MilaToolbox", "ExportExcel", "click");
    } catch (error) {
      notifyErr(
        "Mila is currently unable to generate your Excel file. Please try again shortly."
      );
    }
    setLoadingExcel(false);
  };

  const handleCommentSubmit = async (commentText) => {
    const msg = {
      comment: commentText,
      userId: userData?.userId,
      ouId: userData?.ouId,
      requestId: requestId,
      threadId: threadId || inputThreadId,
      responseNumber: commentIndex,
    };

    if (commentId) {
      msg.id = commentId;
    }

    var result = await apiClient.sendComment(msg);
    if (result) {
      setShowSuccessMessage(true);
      setShowCommentModal(false);
      setTimeout(() => setShowSuccessMessage(false), 2000);
    } else {
      notifyErr("An error occurred. Please try again.");
    }
    setCommmentId(null);
    analytics?.trackEventAnalytics("MilaToolbox", "CommentSubmit", "click");
  };

  const handleCopy = async (text) => {
    window.focus();
    try {
      await navigator.clipboard.writeText(text);
      notify("Response copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy text: ", error);
      notify("Failed to copy text to clipboard. Please try again.");
    }
    analytics?.trackEventAnalytics("MilaToolbox", "CopyResponse", "click");
  };

  const likeThisAnswer = async (like, index) => {
    const msg = {
      comment: like === true ? "Liked" : "Not Liked",
      userId: userData?.userId,
      ouId: userData?.ouId,
      requestId: requestId,
      liked: like,
      threadId: threadId || inputThreadId,
      responseNumber: index,
    };

    return await apiClient.sendComment(msg);
  };

  const handleLike = async (like, index) => {
    if (like === true) {
      notify("I'm glad you liked my answer!");
    }

    let result = await likeThisAnswer(like, index);
    if (result && result.id) {
      setCommmentId(result.id);
      setLiking(like);
    }

    if (!result) {
      notifyErr("An error occurred. Please try again.");
    }
    analytics?.trackEventAnalytics(
      "MilaToolbox",
      "LikeAnswer",
      like === true ? "like" : "dislike"
    );
  };

  const pushResponse = async (resp) => {
    setConversation((prevConversation) => {
      const lastConversationEntry =
        prevConversation[prevConversation.length - 1];
      return [
        ...prevConversation.slice(0, -1),
        {
          ...lastConversationEntry,
          response: lastConversationEntry.response + resp,
        },
      ];
    });
  };

  const uploaderResetRef = useRef(null);

  const handleSend = async () => {
    if ((message && message !== "") || milaContext?.uploadedFiles?.length > 0) {
      setIsLoading(true);
      if (connection) {
        try {
          await executeSend(inputThreadId || threadId, message);
          if (uploaderResetRef) {
            uploaderResetRef.current();
          }
        } catch (error) {
          console.error(error);
          if (
            error?.message ===
            "Cannot send data if the connection is not in the 'Connected' State."
          ) {
            reconnect();
            try {
              console.log(inputThreadId || threadId);
              await executeSend(inputThreadId || threadId, message, false);
              if (uploaderResetRef) {
                uploaderResetRef.current();
              }
            } catch (e) {
              pushResponse(milaUnavailableText);
            }
          }

          console.log("Resetting Mila UI states");
          setIsLoading(false);
          setEnableInput(true);
        }
      }
    }
    analytics?.trackEventAnalytics(
      "MilaToolbox",
      "Ask Mila Prompting",
      "click"
    );
  };

  const handleStopPrompt = async () => {
    if (activeConnection) {
      try {
        console.log("Stopping connection...");
        await connection.stop();

        setIsStopReady(false);

        await connection.start();
        console.log("Connected to Mila Hub!");
        console.log("Resetting Mila UI states");
        setIsLoading(false);
        setEnableInput(true);
        setActiveConnection(null);

      } catch (error) {
        console.error("Error stopping connection:", error);
      }
      analytics?.trackEventAnalytics(
        "MilaToolbox",
        "Ask Mila Stop Prompting",
        "click"
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && enableInput) {
      handleSend();
    }
  };

  const ensureConnected = async (connection, maxRetries = 3) => {
    let retries = 0;

    while (retries < maxRetries) {
      // Check if connection is already connected
      if (connection.state === signalR.HubConnectionState.Connected) {
        console.log("Connection is ready");
        return true;
      }

      // If disconnected or closed, try to reconnect
      if (
        connection.state === signalR.HubConnectionState.Disconnected ||
        connection.state === signalR.HubConnectionState.Closed
      ) {
        try {
          console.log("Attempting to reconnect...");
          await connection.start();
          console.log("Reconnected successfully");
          return true;
        } catch (error) {
          console.error(`Reconnection attempt ${retries + 1} failed:`, error);
          retries++;

          if (retries < maxRetries) {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * (retries + 1)));
          }
        }
      }

      // If connecting or reconnecting, wait for it
      if (
        connection.state === signalR.HubConnectionState.Connecting ||
        connection.state === signalR.HubConnectionState.Reconnecting
      ) {
        console.log("Connection is in progress, waiting...");
        // Wait a bit and check again
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    throw new Error("Failed to establish connection after retries");
  };

  const executeSend = async (tId, msg, initMsg = true) => {
    try {
      fallbackMessages.current = [];
      setProcessingMessage('');
      console.log("Sending message to Mila with tId:", tId);
      let queryFiles = '';
      if (msg === '' && milaContext?.uploadedFiles && milaContext.uploadedFiles.length > 0) {
        queryFiles = 'Provide an abstract of the following files: ' + milaContext?.uploadedFiles?.map(file => `"${file.file_name}"`).join(", ");
        setTitleUploadFile(queryFiles);
      }

      const isOnboardingEntry = onboardingRef.current?.isActiveThread(tId) === true;

      if (initMsg === true) {
        setConversation((prev) => [...prev, { question: msg, response: "", isOnboarding: isOnboardingEntry, ...(isOnboardingEntry ? { pending: true } : {}) }]);
        if (responseEndRef.current) {
          responseEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }
      setEnableInput(false);
      setIsLoading(true);
      setIsStopReady(true);

      // Topic Guard routing (design §13): while the onboarding profile is active, every message
      // on the onboarding thread goes to OnboardingHubV1 — it can never reach the Python Mila AI.
      const isOnboardingTurn = onboardingRef.current?.isActiveThread(tId) === true;
      if (isOnboardingTurn && !onboardingConnection) {
        throw new Error("Onboarding connection is not ready yet.");
      }
      const targetConnection = isOnboardingTurn ? onboardingConnection : connection;
      const targetMethod = isOnboardingTurn ? "ProcessOnboardingPrompt" : "ProcessPrompt";

      if (isOnboardingTurn) {
        setOnboardingSuggestions([]);
      }

      await ensureConnected(targetConnection);

      const connectionPromise = targetConnection.invoke(
        targetMethod,
        Number(userData?.ouId),
        msg ? msg : queryFiles,
        isOnboardingTurn ? null : (requestId != null ? requestId : null),
        tId,
        Number(userData?.userId),
        milaContext?.language || "en-GB"
      );
      setActiveConnection(connectionPromise);

      await connectionPromise;
      setActiveConnection(null);
      setMessage("");
      setCurrentMessageInBox("");
      setSearchMessage("");
      setThreadId(null);
    } catch (e) {
      throw e;
    }
  };

  const handleSearch = async (query) => {
    setMessage(query);
    setCurrentMessageInBox(query);
    if ((query && query !== "") || milaContext?.uploadedFiles?.length > 0) {
      setIsLoading(true);
      if (connection) {
        try {
          await executeSend(inputThreadId || threadId, query);
        } catch (error) {
          console.error(error);
          console.log(error?.message);
          if (
            error?.message ===
            "Cannot send data if the connection is not in the 'Connected' State."
          ) {
            reconnect();
            try {
              await executeSend(inputThreadId || threadId, query, false);
            } catch (e) {
              pushResponse(milaUnavailableText);
            }
          }
          console.log("Resetting Mila UI states");
          setIsLoading(false);
          setEnableInput(true);
        }
      }
    }
    analytics?.trackEventAnalytics(
      "MilaToolbox",
      "Ask Mila Prompting",
      "click"
    );
  };

  const handleSaveHistoric = async () => {
    if (conversation && conversation.length > 0) {
      try {
        let convWithoutEmptyResponses = conversation.filter(c => c.response !== '' && c.response !== null && c.response !== milaUnavailableText);
        let currentCharts = charts;
        let currentTables = tables;
        let conv = convWithoutEmptyResponses[0];

        if (currentCharts && Object.keys(currentCharts).length > 0) {
          //Filter charts to keep only those related to the current thread
          currentCharts = currentCharts[inputThreadId || threadId];
        };

        if (currentTables && Object.keys(currentTables).length > 0) {
          //Filter tables to keep only those related to the current thread
          currentTables = currentTables[inputThreadId || threadId];
        };

        //convWithoutEmptyResponses contains an array of object with properties : question, response
        if (milaContext?.uploadedFiles && milaContext.uploadedFiles.length > 0) {
          convWithoutEmptyResponses?.forEach((conv, index) => {
            // Find all uploaded files that match this conversation's question_index
            const matchingFiles = milaContext.uploadedFiles.filter(file => file.question_index === index && file.thread_id === (threadId || inputThreadId));

            if (matchingFiles.length > 0) {
              conv.uploadedFiles = matchingFiles;
            }
          });
        }

        if (conv) {
          const historic = {
            title: conv.question || titleUploadFile,
            userId: userData?.userId,
            ouId: userData?.ouId,
            response: convWithoutEmptyResponses,
            requestId: requestId,
            charts: currentCharts,
            tables: currentTables,
          };

          await apiClient.saveHistoric(historic);

          var h = await apiClient.getMilaHistoric(userData?.userId);
          setMilaHistoricData(h);
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleFavorite = async (conv) => {
    if (conv) {
      if (conv.isFavorite === true) {
        conv.isFavorite = false;
      } else {
        conv.isFavorite = true;
      }

      let titleFile = titleUploadFile;
      if (!titleFile && conversation && conversation.length > 0 && conversation[0].uploadedFiles && conversation[0].uploadedFiles.length > 0) {
        let fn = conversation[0].uploadedFiles[0].file_name;
        titleFile = `Provide an abstract of the following files: ${fn}`;
      }

      const favorite = {
        title: conv.question || titleFile,
        name: conv.question || titleFile,
        userId: userData?.userId,
        ouId: userData?.ouId,
        response: conversation,
        requestId: requestId,
      };

      conv.isFavorite = await apiClient.setFavorites(favorite);

      //Refresh list of favorites
      var f = await apiClient.getFavorites(userData?.userId);
      setFavorites(f);
    }
    analytics?.trackEventAnalytics("MilaToolbox", "Add Favorite", "click");
  };

  // The onboarding thread keeps the hero ("Hey <name>" + avatar) above the conversation,
  // per the design's "Introduction" page — the intro streams under it, same surface.
  const isOnboardingThreadView =
    !!onboarding.onboardingThreadId &&
    (inputThreadId || threadId) === onboarding.onboardingThreadId;

  // An unfinished onboarding (status InProgress/Declined) survives sign-out, so on the next
  // login the surface must still be the NORMAL Mila home — the onboarding UI belongs to the
  // onboarding thread only, which the user re-opens from history when they want to continue.
  const isOnboardingSurface = isOnboardingThreadView && onboarding.isConversationActive;

  // While the scripted flow runs the user is answering Mila, so the input hints at that.
  // Once it ends (status Completed) the chips become questions to ask Mila — and
  // isConversationActive turns false — so the normal chat placeholder comes back.
  const inputPlaceholder = isOnboardingSurface
    ? "Type your response here or use a suggested response"
    : "Type your question here";

  const hideCommentModal = () => setShowCommentModal(false);
  const [currentPrompt, setCurrentPrompt] = useState(null);

  const handleSelectedPrompt = (prompt) => {
    setCurrentPrompt(prompt);
  };

  const [selectedSchedulableQuestion, setSelectedSchedulableQuestion] = useState(null);
  // Files uploaded to the discussion being scheduled — the first schedule popup warns that
  // the scheduled prompt will run without them
  const [scheduleUploadedFiles, setScheduleUploadedFiles] = useState([]);

  const handleOnContinueSchedule = (selectedQuestion) => {
    setShowPromptSelectionSchedule(false);
    setShowScheduler(true);
    setSelectedSchedulableQuestion(selectedQuestion);
    // Continue answered the warning: proceed without the files, don't ask again
    setScheduleUploadedFiles([]);
  };

  // A single file upload without a prompt has nothing schedulable yet
  const isScheduleDisabled = (conv) =>
    conversation?.length === 1 && !conv.question?.trim();

  const handleSchedulePrompt = (conv) => {
    if (isScheduleDisabled(conv)) {
      return;
    }
    setScheduleUploadedFiles(
      discussionUploadedFiles(conversation, milaContext?.uploadedFiles, [threadId, inputThreadId].filter(Boolean))
    );
    // A thread started by a file upload without a prompt has no original
    // prompt to offer — follow-up questions only make sense in context
    const startedWithFileOnly = !conversation?.[0]?.question?.trim();
    setCurrentQuestion(startedWithFileOnly ? null : conv.question);
    if (conversation?.length > 1) {
      setShowPromptSelectionSchedule(true);
    } else if (conversation?.length === 1) {
      setSelectedSchedulableQuestion(conv.question);
      setShowScheduler(true);
      setShowPromptSelectionSchedule(false);
    }
  }

  return (
    <>
      <Modal
        open={showPromptSelectionSchedule}
        onClose={() => setShowPromptSelectionSchedule(false)}
      >
        <PromptSelectionSchedule
          questions={conversation}
          currentQuestion={currentQuestion}
          uploadedFiles={scheduleUploadedFiles}
          onClose={() => setShowPromptSelectionSchedule(false)}
          onContinueSchedule={(question) => handleOnContinueSchedule(question)}
        />
      </Modal>
      <Modal open={showScheduler} onClose={() => setShowScheduler(false)}>
        <NotificationScheduler
          selectedQuestion={selectedSchedulableQuestion}
          onClose={() => setShowScheduler(false)}
          onSchedule={handleSchedule}
          handleSelectedPrompt={handleSelectedPrompt}
          userData={userData}
          uploadedFiles={scheduleUploadedFiles}
        />
      </Modal>
      <Modal open={showCommentModal} onClose={() => setShowCommentModal(false)}>
        <CommentModal
          showSuccessMessage={showSuccessMessage}
          hideCommentModal={hideCommentModal}
          handleCommentSubmit={handleCommentSubmit}
        />
      </Modal>
      {userData !== null && (
        <div className="flex flex-col font-notosans flex-1 min-h-0">
          <Tooltip
            id="tooltipActions"
            border="1px solid rgba(202, 212, 231, 0.50)"
            background="white"
            opacity={1}
            noArrow
            style={{
              borderRadius: "12px",
              background: "#FFF",
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.03)",
              padding: "12px 16px",
              color: "var(--dark-blue)",
              fontFamily: "Noto Sans",
              fontSize: "12px",
              lineHeight: "16px",
              maxWidth: 500,
              zIndex: 9999,
            }}
          />
          <div
            ref={conversationScrollRef}
            onScroll={scheduledFeed.onScroll}
            onWheel={scheduledFeed.onWheel}
            className={clsx(
              "scrollbar-thin overflow-auto flex flex-col flex-1 mt-16",
              // Onboarding: the input card + suggested responses below must always stay in
              // view — let flexbox size the scroll area instead of a fixed max-height.
              isOnboardingThreadView
                ? "min-h-0"
                : "h-lvh max-h-[78vh] md:max-h-[75vh]",
              { "!mt-25 justify-between": conversation.length === 0 }
            )}
          >
            {/* Onboarding is starting but the intro hasn't streamed yet: show the onboarding
                surface (hero + typing dots) — never the normal home page (no flash).
                Only on the onboarding thread itself: an empty normal thread must never wait
                on a turn that is not coming. */}
            {conversation && conversation.length === 0 && isOnboardingSurface && (
              <div className="w-full mx-auto px-4 lg:px-10 max-w-[900px] pt-6">
                <UserProfile
                  firstName={userData?.firstName}
                  lastName={userData?.lastName}
                  hideSubtitle={true}
                />
                <div className="flex items-center gap-3 text-[14px]">
                  <img src={icon} alt="Mila Avatar" />
                  <span className="loaderChat"></span>
                </div>
              </div>
            )}
            {conversation && conversation.length === 0 && !isOnboardingSurface && (
              <div className="flex flex-col flex-1 2xl:max-w-[896px] px-7 w-full mx-auto">
                <div className="max-h-[530px] sm:max-h-[430px]">
                  <UserProfile
                    firstName={userData?.firstName}
                    lastName={userData?.lastName}
                  />
                  <SearchBar onSearch={handleSearch}
                    question={searchMessage}
                    isUploading={isUploading}
                    onStartedTyping={() => setShowSuggested(false)}
                    UploaderComponent={<MilaUploader
                      threadId={inputThreadId || threadId}
                      currentQuestionIndex={0}
                      onError={handleUploadError}
                      onFilesReady={handleFilesReady}
                      onResetUploader={(resetAll) => {
                        setResetUploaderFunction(resetAll);
                        onResetUploader(resetAll);
                      }}
                      onUploadStatusFinished={(status, files) => {
                        if (status === true) {
                          setIsUploading(false);
                          onFilesUploaded(files);
                        }
                        else {
                          setIsUploading(true);
                        }
                      }}
                    />}
                  />
                </div>
                {showSuggested === true && !isOnboardingSurface &&
                  <Suggested
                    userId={userData?.userId}
                    onSuggestedClick={(q) => {
                      setMessage(q);
                      setSearchMessage(q);
                      setThreadId(null);
                      onExternalMessageHandled();
                    }}
                  />
                }
              </div>
            )}
            {conversation && conversation.length > 0 && isOnboardingThreadView && (
              <div className="w-full mx-auto px-4 lg:px-10 max-w-[900px] pt-6">
                <UserProfile
                  firstName={userData?.firstName}
                  lastName={userData?.lastName}
                  hideSubtitle={true}
                />
              </div>
            )}
            {conversation &&
              conversation.length > 0 &&
              (conversation[0].date === undefined ? (
                conversation.map((conv, index) => (
                  <div
                    key={index}
                    className="w-full mx-auto flex flex-col gap-3 lg:gap-3 px-4 lg:px-10 text-[14px] max-w-[900px]"
                  >
                    {conv.question ? <Question>{conv.question}</Question> : <></>}
                    <FilesContainer>{milaContext?.triggers?.displayFiles(threadId || inputThreadId, index, conv.uploadedFiles)}</FilesContainer>
                    <Response
                      key={"k_" + (charts ? Object.keys(charts).length : 0)}
                      isLastItem={index === conversation.length - 1}
                      isLoading={isLoading}
                      loadingExcel={loadingExcel}
                      favorites={favorites}
                      conv={conv}
                      onFavoriteClick={() => handleFavorite(conv)}
                      onCopyClick={() => {
                        handleCopy(conv.response);
                      }}
                      onLikeClick={(value) => handleLike(value, index)}
                      showCommentModal={() => {
                        setCommentIndex(index);
                        setShowCommentModal(true);
                      }}
                      exportPdf={() => handleExportToPdf(conv.response)}
                      exportExcel={() => handleExportToExcel(conv.response, responseTables(tables, threadId || inputThreadId))}
                      onScheduleClick={() => {
                        handleSchedulePrompt(conv);
                      }}
                      scheduleDisabled={isScheduleDisabled(conv)}
                      charts={charts}
                      tables={tables}
                      processingMessage={processingMessage}
                      threadId={threadId || inputThreadId}
                    >
                      {
                        conv.response !== '' ?
                          conv.response :
                          ((index === 0 && isLoading && conversation.length === 1) || (index === conversation.length - 1 && isLoading)) ?
                            conv.response :
                            'Mila was stopped before returning a response.'
                      }
                    </Response>
                  </div>
                ))
              ) : (
                <div className="w-full mx-auto px-4 text-[14px] flex flex-col gap-3 lg:gap-3 lg:px-10 text-[14px] max-w-[900px]">
                  <Question>{conversation[0].question}</Question>
                  {scheduledFeed.loading && (
                    // Out of the flow (a zero-height row, its own gap-3 cancelled): the loader must
                    // not push the answers down, or the viewport the hook restores after the
                    // prepend is off by its height.
                    <div className="relative h-0 overflow-visible -mt-3">
                      <div
                        className="absolute left-1/2 top-1 -translate-x-1/2 flex items-center gap-2 rounded-full bg-white px-3 py-1 text-dimgray-200 text-[13px] shadow-sm border border-[#F0E5FB]"
                        role="status"
                      >
                        <span className="loaderChat"></span>
                        Loading earlier answers…
                      </div>
                    </div>
                  )}
                  {!scheduledFeed.loading && scheduledFeed.feed && !scheduledFeed.feed.hasMore && scheduledFeed.pagesLoaded > 0 && (
                    <div className="py-3 text-center text-dimgray-200 text-[13px]">
                      This is the first answer of this report.
                    </div>
                  )}
                  {conversation.map((conv, index) => (
                    <div key={conv.id !== undefined ? `sr_${conv.id}` : index}>
                      {conv.date === undefined && conv.question && (
                        <Question>{conv.question}</Question>
                      )}
                      <FilesContainer>{milaContext?.triggers?.displayFiles(threadId || inputThreadId, index, conv.uploadedFiles)}</FilesContainer>
                      {conv?.response &&
                        <Response
                          key={"k_" + (conv.charts ? Object.keys(conv.charts).length : 0)}
                          isLastItem={index === conversation.length - 1}
                          isLoading={isLoading}
                          loadingExcel={loadingExcel}
                          favorites={favorites}
                          conv={conv}
                          onFavoriteClick={() => handleFavorite(conv)}
                          onCopyClick={() => {
                            handleCopy(conv.response);
                          }}
                          onLikeClick={(value) => handleLike(value, index)}
                          showCommentModal={() => {
                            setCommentIndex(index);
                            setShowCommentModal(true);
                          }}
                          exportPdf={() => handleExportToPdf(conv.response)}
                          exportExcel={() => handleExportToExcel(conv.response, responseTables(conv.tables, threadId || inputThreadId))}
                          onScheduleClick={() => {
                            handleSchedulePrompt(conv);
                          }}
                          scheduleDisabled={isScheduleDisabled(conv)}
                          charts={conv.charts}
                          tables={conv.tables}
                          threadId={threadId || inputThreadId}
                          processingMessage={processingMessage}
                        >
                          {
                            conv.response !== '' ?
                              conv.response :
                              ((index === 0 && isLoading && conversation.length === 1) || (index === conversation.length - 1 && isLoading)) ?
                                conv.response :
                                'Mila was stopped before returning a response.'
                          }
                        </Response>}
                    </div>
                  ))}
                </div>
              ))}
            <div ref={responseEndRef} />
          </div>
          {conversation && conversation.length > 0 &&
            <div
              className={clsx(
                // shrink-0: the input card must never be crushed or pushed out of view by
                // the growing conversation — it stays pinned while responses stream.
                "flex w-[95%] mx-auto mt-auto px-4 text-[14px] lg:px-0 flex flex-col peer border border-[#F0E5FB] opacity-1 rounded-[20px] overflow-hidden max-w-[900px] shrink-0",
                {
                  "text-[#969696]": !enableInput,
                  // breathing room between the card and the bottom of the viewport
                  "mb-5": isOnboardingThreadView,
                }
              )}
            >
              <NounAwareTextArea
                rowsCount="1"
                placeholderLabel={inputPlaceholder}
                value={message}
                onChangeEvent={(e) => {
                  setMessage(e.target.value);
                  if (setCurrentMessageInBox) {
                    setCurrentMessageInBox(e.target.value);
                  }
                }}
                disabledState={!enableInput}
                requiredField={true}
                classNames={clsx(
                  "noun-textarea w-full pl-7 pt-3 px-3 py-3 peer focus:outline-none focus:border-blue-200 text-black opacity-1 placeholder:text-dimgray-400 resize-none disabled:bg-transparent disabled:cursor-not-allowed",
                  {
                    "disabled:text-[#969696]": !enableInput,
                  }
                )}
                onStartedTyping={setShowSuggested}
                detections={detections}
                onReplace={handleNounReplace}
                rootOU={rootOU}
                mentionSuggestions={mentionSuggestions}
                isMentionLoading={isMentionLoading}
                onMentionQueryChange={setMentionQuery}
                onMentionApply={handleMentionApply}
              />
              <div className="flex justify-between items-center px-3 py-0 pb-[8px]">
                {isOnboardingThreadView ? (
                  // File upload is a normal-chat capability — never offered during onboarding
                  <div />
                ) : (
                  <MilaUploader
                    threadId={inputThreadId || threadId}
                    onError={handleUploadError}
                    onFilesReady={handleFilesReady}
                    currentQuestionIndex={conversation.length}
                    disableButton={!enableInput}
                    onResetUploader={(resetAll) => {
                      uploaderResetRef.current = resetAll;
                      onResetUploader(resetAll);
                    }}
                    onUploadStatusFinished={(status, files) => {
                      if (status === true) {
                        setIsUploading(false);
                        onFilesUploaded(files);
                      }
                      else {
                        setIsUploading(true);
                      }
                    }}
                  />
                )}

                <div className="flex items-center space-x-5">
                  <button
                    type="button"
                    className="flex items-center justify-center h-8 w-8 rounded-full bg-[#F3F1FF]"
                    style={{ visibility: "hidden" }}
                  >
                    <svg width="12" height="17" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M3.44731 1.04258C4.86458 -0.347527 7.13484 -0.347527 8.55454 1.04258C9.21128 1.68673 9.61749 2.57288 9.61749 3.54762V8.35843C9.61749 10.3057 7.98656 11.9059 6.00064 11.9059C4.01532 11.9059 2.38378 10.3063 2.38378 8.35843V3.54762C2.38378 2.57292 2.79226 1.68677 3.44673 1.04258H3.44731ZM6.00131 0.999047C4.57802 0.999047 3.40378 2.15157 3.40378 3.54678V8.35759C3.40378 9.75359 4.57883 10.9053 6.00131 10.9053C7.4246 10.9053 8.59883 9.75279 8.59883 8.35759V3.54678C8.59883 2.15078 7.42379 0.999047 6.00131 0.999047ZM3.12396 17H8.87808C9.15942 17 9.38811 16.775 9.38811 16.4997C9.38811 16.2238 9.15867 15.9995 8.87808 15.9995H6.51146V14.222C9.58677 13.9652 12 11.4166 12 8.3591C12 8.08315 11.7706 7.85884 11.49 7.85884C11.2086 7.85884 10.9799 8.08389 10.9799 8.3591C10.9799 11.0449 8.73811 13.2436 6 13.2436C3.26189 13.2436 1.02006 11.0447 1.02006 8.3591C1.02006 8.08315 0.79062 7.85884 0.51003 7.85884C0.228689 7.85884 0 8.08389 0 8.3591C0 11.4174 2.41323 13.9653 5.48854 14.222V15.9995H3.12192C2.84058 15.9995 2.61189 16.2245 2.61189 16.4997C2.61189 16.7757 2.84133 17 3.12192 17H3.12396Z" fill="#303C54" />
                    </svg>
                  </button>
                  <div className="py-3 pr-0 rounded-base  rounded-l-none border-l-0 peer-focus:outline-none peer-focus:border-blue-200">
                    <Button
                      type="button"
                      onClick={!isMenuActive ? handleSend : handleStopPrompt}
                      disabled={(!enableInput && isStopReady === false) || isUploading}
                      className="flex items-center justify-center w-[80px] h-[42px] gap-2 bg-dark-blue-100 hover:bg-dark-blue-hover text-[#C4C2F4] text-sl font-notosans font-bold leading-normal rounded-[18px] w-fit p-3 cursor-pointer"
                    >
                      <img src={isStopReady === false || (isStopReady === true && enableInput) ? icon : stopIcon} style={{ "width": "18px" }} alt="star" />
                    </Button>
                  </div>
                </div>
              </div>
              {onboardingSuggestions.length > 0 && !isLoading && isOnboardingThreadView && (
                <OnboardingSuggestedResponses
                  suggestions={onboardingSuggestions}
                  title={onboarding.status === ONBOARDING_STATUS.Completed ? "Suggested questions" : "Suggested responses"}
                  disabled={!enableInput}
                  onSelect={(s) => {
                    setOnboardingSuggestions([]);
                    handleSearch(s);
                  }}
                />
              )}
            </div>
          }
          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme="colored"
          />
        </div>
      )}
      {showTour && <OnboardingTour onComplete={handleTourComplete} />}
      <GrtAnalyticsGA4 onLoaded={handleAnalytics} />

    </>
  );
};

export default MilaAiPage;
